import type {
  VariantAttributeDefinitionViewModel,
  VariantAttributeFormValue,
} from '@workspace/silk-ui';

import type {
  CommerceAttributeDefinition,
  ProductVariantAttributeValue,
} from '@/lib/commerce';

type ValidationMap = Record<string, string>;

const bandwidthFactors = {
  Mbps: 1_000_000,
  Gbps: 1_000_000_000,
} as const;

type VariantAttributePayload = {
  attribute_key: string;
  integer_value?: number;
  decimal_value?: number;
  text_value?: string;
  boolean_value?: boolean;
  object_value?: Record<string, unknown>;
};

export function attributeDefinitionsToViewModels(
  definitions: CommerceAttributeDefinition[],
): VariantAttributeDefinitionViewModel[] {
  return definitions.map(definition => ({
    attributeKey: definition.attribute_key,
    displayName: definition.display_name,
    valueType: definition.value_type,
    quantityDimension: definition.quantity_dimension,
    canonicalUnit: definition.canonical_unit,
    validationRules: definition.validation_rules || {},
  }));
}

function formatScaledInteger(value: number, unit: keyof typeof bandwidthFactors): string {
  if (!Number.isSafeInteger(value)) {
    return String(value / bandwidthFactors[unit]);
  }
  const factor = bandwidthFactors[unit];
  const whole = Math.trunc(value / factor);
  const remainder = Math.abs(value % factor);
  if (remainder === 0) {
    return String(whole);
  }
  const fractional = String(remainder).padStart(String(factor).length - 1, '0').replace(/0+$/, '');
  return `${whole}.${fractional}`;
}

function parseScaledInteger(value: string, unit: keyof typeof bandwidthFactors): number | null {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) {
    return null;
  }
  const factor = bandwidthFactors[unit];
  const precision = String(factor).length - 1;
  const [wholePart, fractionalPart = ''] = normalized.split('.');
  if (fractionalPart.length > precision) {
    return null;
  }
  const scaled = BigInt(wholePart) * BigInt(factor) + BigInt((fractionalPart + '0'.repeat(precision)).slice(0, precision));
  const result = Number(scaled);
  return Number.isSafeInteger(result) ? result : null;
}

export function variantAttributesToForm(
  attributes: ProductVariantAttributeValue[] | undefined,
): VariantAttributeFormValue[] {
  return (attributes || []).map((attribute, index) => {
    const isBandwidth = attribute.quantity_dimension === 'bandwidth' && attribute.canonical_unit === 'bps';
    const integerValue = attribute.integer_value ?? 0;
    const unit = isBandwidth && Math.abs(integerValue) >= bandwidthFactors.Gbps ? 'Gbps' : 'Mbps';
    let value = '';
    switch (attribute.value_type) {
      case 'integer':
        value = isBandwidth ? formatScaledInteger(integerValue, unit) : String(attribute.integer_value ?? '');
        break;
      case 'decimal':
        value = String(attribute.decimal_value ?? '');
        break;
      case 'boolean':
        value = String(attribute.boolean_value ?? false);
        break;
      case 'text':
        value = attribute.text_value || '';
        break;
      case 'object':
        value = JSON.stringify(attribute.object_value || {}, null, 2);
        break;
    }
    return {
      id: `persisted-${index}-${attribute.attribute_key}`,
      attributeKey: attribute.attribute_key,
      value,
      unit,
    };
  });
}

function payloadForRow(
  row: VariantAttributeFormValue,
  definition: VariantAttributeDefinitionViewModel,
): VariantAttributePayload | null {
  const value = row.value.trim();
  const payload: VariantAttributePayload = { attribute_key: row.attributeKey };
  if (definition.quantityDimension === 'bandwidth' && definition.canonicalUnit === 'bps') {
    const integerValue = parseScaledInteger(value, row.unit);
    return integerValue === null ? null : { ...payload, integer_value: integerValue };
  }
  switch (definition.valueType) {
    case 'integer': {
      const integerValue = Number(value);
      return Number.isSafeInteger(integerValue) ? { ...payload, integer_value: integerValue } : null;
    }
    case 'decimal': {
      const decimalValue = Number(value);
      return Number.isFinite(decimalValue) ? { ...payload, decimal_value: decimalValue } : null;
    }
    case 'boolean':
      return value === 'true' || value === 'false' ? { ...payload, boolean_value: value === 'true' } : null;
    case 'text':
      return value ? { ...payload, text_value: value } : null;
    case 'object': {
      try {
        const objectValue: unknown = JSON.parse(value);
        return objectValue && typeof objectValue === 'object' && !Array.isArray(objectValue)
          ? { ...payload, object_value: objectValue as Record<string, unknown> }
          : null;
      } catch {
        return null;
      }
    }
  }
}

export function buildVariantAttributePayload(
  rows: VariantAttributeFormValue[],
  definitions: VariantAttributeDefinitionViewModel[],
): VariantAttributePayload[] {
  const definitionByKey = new Map(definitions.map(definition => [definition.attributeKey, definition]));
  return rows.map(row => {
    const definition = definitionByKey.get(row.attributeKey);
    if (!definition) {
      throw new Error(`Unknown attribute definition ${row.attributeKey || '(empty)'}.`);
    }
    const payload = payloadForRow(row, definition);
    if (!payload) {
      throw new Error(`Invalid value for ${definition.displayName}.`);
    }
    return payload;
  });
}

export function validateVariantAttributes(
  rows: VariantAttributeFormValue[],
  definitions: VariantAttributeDefinitionViewModel[],
): ValidationMap {
  const errors: ValidationMap = {};
  const definitionByKey = new Map(definitions.map(definition => [definition.attributeKey, definition]));
  const seen = new Set<string>();
  const canonicalValues = new Map<string, number>();

  rows.forEach(row => {
    const prefix = `attribute.${row.id}`;
    if (!row.attributeKey) {
      errors[`${prefix}.definition`] = 'Select an attribute.';
      return;
    }
    if (seen.has(row.attributeKey)) {
      errors[`${prefix}.definition`] = 'Each attribute can only be assigned once.';
      return;
    }
    seen.add(row.attributeKey);
    const definition = definitionByKey.get(row.attributeKey);
    if (!definition) {
      errors[`${prefix}.definition`] = 'This attribute definition is unavailable.';
      return;
    }
    const payload = payloadForRow(row, definition);
    if (!payload) {
      errors[`${prefix}.value`] = definition.valueType === 'object'
        ? 'Enter a valid JSON object.'
        : `Enter a valid ${definition.valueType} value.`;
      return;
    }
    if (row.attributeKey === 'broadband.consumer_label' && payload.object_value) {
      const message = validateBroadbandFactsConfiguration(payload.object_value);
      if (message) errors[`${prefix}.value`] = message;
    }
    const numericValue = payload.integer_value ?? payload.decimal_value;
    if (numericValue !== undefined) {
      if (definition.validationRules.minimum_exclusive !== undefined && numericValue <= Number(definition.validationRules.minimum_exclusive)) {
        errors[`${prefix}.value`] = `Value must be greater than ${definition.validationRules.minimum_exclusive}.`;
      }
      if (definition.validationRules.minimum !== undefined && numericValue < Number(definition.validationRules.minimum)) {
        errors[`${prefix}.value`] = `Value must be at least ${definition.validationRules.minimum}.`;
      }
      if (definition.validationRules.maximum_exclusive !== undefined && numericValue >= Number(definition.validationRules.maximum_exclusive)) {
        errors[`${prefix}.value`] = `Value must be less than ${definition.validationRules.maximum_exclusive}.`;
      }
      if (definition.validationRules.maximum !== undefined && numericValue > Number(definition.validationRules.maximum)) {
        errors[`${prefix}.value`] = `Value must be no greater than ${definition.validationRules.maximum}.`;
      }
      canonicalValues.set(row.attributeKey, numericValue);
    }
    if (payload.text_value !== undefined) {
      if (definition.validationRules.minimum_length !== undefined && payload.text_value.length < Number(definition.validationRules.minimum_length)) {
        errors[`${prefix}.value`] = `Value must contain at least ${definition.validationRules.minimum_length} characters.`;
      }
      if (definition.validationRules.maximum_length !== undefined && payload.text_value.length > Number(definition.validationRules.maximum_length)) {
        errors[`${prefix}.value`] = `Value must contain no more than ${definition.validationRules.maximum_length} characters.`;
      }
    }
  });

  const ratePairs = [
    ['network.committed_download_rate', 'network.download_rate'],
    ['network.committed_upload_rate', 'network.upload_rate'],
  ] as const;
  for (const [committedKey, maximumKey] of ratePairs) {
    const committed = canonicalValues.get(committedKey);
    const maximum = canonicalValues.get(maximumKey);
    if (committed !== undefined && maximum !== undefined && committed > maximum) {
      const row = rows.find(item => item.attributeKey === committedKey);
      if (row) {
        errors[`attribute.${row.id}.value`] = 'Committed rate cannot exceed the corresponding maximum rate.';
      }
    }
  }
  return errors;
}

export function mapVariantAttributeServiceError(
  message: string,
  rows: VariantAttributeFormValue[],
): ValidationMap | null {
  const match = message.match(/attributes\[(\d+)]/);
  if (!match) {
    return null;
  }
  const row = rows[Number(match[1])];
  if (!row) {
    return null;
  }
  return { [`attribute.${row.id}.value`]: message.replace(/^invalid sales workspace input:\s*/i, '') };
}

export function initialVariantAttributeValue(attributeKey: string | undefined, valueType: string | undefined): string {
  if (attributeKey === 'broadband.consumer_label') {
    return JSON.stringify({ status: 'draft', service_type: 'fixed', performance_is_placeholder: true,
      provider_name: '', service_plan_name: '', fcc_registration_number: '',
      typical_download_speed_mbps: 1000, typical_upload_speed_mbps: 1000, typical_latency_ms: 10,
      discounts_and_bundles_url: '', network_management_policy_url: '', privacy_policy_url: '',
      customer_support_phone: '', customer_support_url: '' });
  }
  return valueType === 'boolean' ? 'false' : '';
}

function validateBroadbandFactsConfiguration(value: Record<string, unknown>): string | null {
  if (value.status !== 'draft' && value.status !== 'published') return 'Select Draft or Published.';
  if (value.service_type !== 'fixed' && value.service_type !== 'mobile') return 'Select a service type.';
  if (typeof value.performance_is_placeholder !== 'boolean') return 'Specify whether performance values are placeholders.';
  if (value.status === 'draft') return null;
  if (value.performance_is_placeholder) return 'Replace placeholder performance values and clear the placeholder flag before publishing.';
  if (!/^[0-9]{10}$/.test(String(value.fcc_registration_number ?? '')) || value.fcc_registration_number === '0000000000') return 'Enter the provider’s real ten-digit FCC registration number.';
  for (const field of ['provider_name', 'service_plan_name', 'discounts_and_bundles_url', 'network_management_policy_url', 'privacy_policy_url', 'customer_support_phone', 'customer_support_url']) {
    if (typeof value[field] !== 'string' || !String(value[field]).trim()) return 'Complete provider, plan, policy and support details before publishing.';
  }
  for (const field of ['typical_download_speed_mbps', 'typical_upload_speed_mbps', 'typical_latency_ms']) {
    if (typeof value[field] !== 'number' || !Number.isFinite(value[field]) || Number(value[field]) < 0) return 'Enter typical download, upload and latency values.';
  }
  const taxes = value.government_taxes as Record<string, unknown> | undefined;
  const data = value.data_allowance as Record<string, unknown> | undefined;
  if (!taxes?.treatment || typeof data?.unlimited !== 'boolean') return 'Configure government taxes and data allowance before publishing.';
  return null;
}
